/**
 * src/lib/i18n.ts
 * =============================================================================
 * Configuración de internacionalización (react-i18next).
 *
 * Idiomas activos (2026-05-24):
 *   - es: español (default)
 *   - en: inglés
 *   - de: alemán
 *   - fr: francés
 *   - it: italiano
 *   - pt: portugués
 *
 * Detección automática:
 *   `i18next-browser-languagedetector` recorre el orden ['localStorage',
 *   'navigator']. Si el navegador del usuario reporta `de-DE`, i18next
 *   resuelve a `de` (subtag matching) y la app aparece en alemán sin
 *   ninguna acción del usuario. Si nada matchea, fallback a 'es'.
 *
 * Estrategia de carga:
 *   Importamos estáticamente los JSON. Mientras los namespaces sean dos
 *   (common + auth) y los idiomas sean ~6, el coste de bundle es marginal.
 *   Si crece, migrar a `i18next-http-backend` o a lazy chunks por idioma.
 *
 * Selector de idioma en UI (2026-05-29):
 *   `LocaleSwitcher.tsx` en el footer de la LandingPage permite override
 *   manual del idioma autodetectado — útil cuando el navegador reporta
 *   `de-AT` pero el usuario quiere `en`, o cuando el sistema operativo
 *   está en un idioma distinto al que la persona habla.
 *   La elección se persiste vía `detection.caches: ['localStorage']` ya
 *   configurado más abajo, así que sobrevive a recargas.
 * =============================================================================
 */

import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import LanguageDetector from 'i18next-browser-languagedetector'

import esCommon from '@/locales/es/common.json'
import esAuth from '@/locales/es/auth.json'
import enCommon from '@/locales/en/common.json'
import enAuth from '@/locales/en/auth.json'
import deCommon from '@/locales/de/common.json'
import deAuth from '@/locales/de/auth.json'
import frCommon from '@/locales/fr/common.json'
import frAuth from '@/locales/fr/auth.json'
import itCommon from '@/locales/it/common.json'
import itAuth from '@/locales/it/auth.json'
import ptCommon from '@/locales/pt/common.json'
import ptAuth from '@/locales/pt/auth.json'

export const SUPPORTED_LOCALES = ['es', 'en', 'de', 'fr', 'it', 'pt'] as const
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number]

/** Namespaces disponibles. Coincide con las carpetas bajo src/locales/<idioma>/ */
export const NAMESPACES = ['common', 'auth'] as const

/**
 * Mantiene `<html lang>` en sintonía con el idioma activo.
 *
 * El index.html trae `lang="es"` fijo, pero la app puede resolver a
 * cualquiera de los seis idiomas: un lector de pantalla pronunciaba la
 * interfaz alemana con fonética española, y los correctores/traductores
 * del navegador se guiaban por un idioma que no era el que se veía.
 * Se registra ANTES del init para capturar también el `languageChanged`
 * que i18next emite al detectar el idioma inicial.
 *
 * El guard de `document` es por los tests (vitest corre en entorno node,
 * sin DOM) — ahí simplemente no hay etiqueta que sincronizar.
 */
function sincronizarLangHtml(idioma: string): void {
  if (typeof document === 'undefined') return
  document.documentElement.lang = idioma
}
i18n.on('languageChanged', sincronizarLangHtml)

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      es: { common: esCommon, auth: esAuth },
      en: { common: enCommon, auth: enAuth },
      de: { common: deCommon, auth: deAuth },
      fr: { common: frCommon, auth: frAuth },
      it: { common: itCommon, auth: itAuth },
      pt: { common: ptCommon, auth: ptAuth },
    },
    fallbackLng: 'es',
    supportedLngs: [...SUPPORTED_LOCALES],
    // Aceptar variantes regionales (es-ES, en-GB, pt-BR) reduciendo al
    // subtag base. Sin esto un usuario con navigator.language='de-AT' no
    // matchearía 'de'.
    load: 'languageOnly',
    nonExplicitSupportedLngs: true,
    defaultNS: 'common',
    ns: [...NAMESPACES],
    interpolation: {
      escapeValue: false,
    },
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
      lookupLocalStorage: 'i18nextLng',
    },
  })
  // Cinturón además del listener: si algún día una versión de i18next dejara
  // de emitir `languageChanged` durante el init, el lang quedaría igualmente
  // bien puesto al terminar la inicialización.
  .then(() => sincronizarLangHtml(i18n.language))

export default i18n
